import type { DeepPartialOnlyKeys, RequireOnly } from '@/utils/types';

export interface Option {
  name: string;
  value: string;
}

export interface Location {
  lat: number;
  long: number;
}

export interface GisLocation {
  lat: number;
  long: number;
}

/* eslint-disable no-use-before-define */
export interface Entity {
  id: string;
}

export interface HydraMember {
  '@id': string;
  '@type': string;
  id: string;
}

export interface HydraCollection<T> {
  '@context': string;
  '@id': string;
  '@type': string;
  'hydra:totalItems': number;
  'hydra:member': Array<HydraMember & T>;
}

export interface PagedResult<T> {
  items: T[];
  total: number;
}

export interface TranslationEntity extends HydraMember {
  language: string;
}

export interface TranslatedEntity<T extends TranslationEntity> extends HydraMember {
  translations: T[];
}

export interface WbFile extends HydraMember {
  id: string;
  contentUrl: string;
  originalFileName: string;
  mimeType: string;
  size: number;
}

export interface Region extends HydraMember {
  id: string;
  name: string;
  gisLocation: Location;
}

export type RegionOption = Region & Option;

export interface Address extends HydraMember {
  id: string;
  street: string;
  cityName: string;
  zip: string;
}

export interface LicenseSeat extends HydraMember {
  id: string;
  type?: string;
  license?: License;
  licenseGoal?: LicenseGoal;
  licenseRanking?: LicenseRanking;
  licenseReward?: LicenseReward;
  seats?: number;
  numberOfDrivers?: number;
  company?: Company;
  qrCode?: string;
  verificationStatus?: string;
}

export interface License extends HydraMember {
  id: string;
  startDate?: Date;
  endDate?: Date;
  duration?: number;
  automaticRenewal?: boolean;
  followupLicense?: License;
  transferCompleted?: boolean;
  company?: Company;
  licenseSeats?: LicenseSeat[];
  advertisementEnabled?: boolean;
  blockedAdvertisementCompanies?: string[]|Company[];

  licenseGoal?: LicenseGoal;
  licenseRanking?: LicenseRanking;
  licenseReward?: LicenseReward;

  payoutSchedule?: CompanyPayoutSchedule;

  trackedDistance?: number;
  trackedTime?: number;
  kcal?: number;
  co2?: number;
  numOfTours?: number;
  numOfTopPhotos?: number;
  avgSpeed?: number;
}

export interface LicenseGoal extends HydraMember {
  id: string;
  license?: License;
  // licenseSeat?: LicenseSeat;
  licenseGoalStatements?: GoalStatement[];
  formula?: string;
  maximumPayoutPercentage?: number;
}

export interface GoalStatement extends HydraMember {
  id: string;
  licenseGoal?: LicenseGoal;
  type?: string;
  data?: { target: number };
}

export interface LicenseRanking extends HydraMember {
  id: string;
  licenseRankingStatements?: RankingStatement[];
}

export interface RankingStatement extends HydraMember {
  id: string;
  rankingType?: string;
  position?: number;
}

export interface LicenseReward extends HydraMember {
  id: string;
  licenseBounties?: LicenseBounty[];
}

export interface LicenseBounty extends HydraMember {
  id: string;
  licenseReward?: LicenseReward;
  rewardType?: string;
  amount?: number;
}

export interface CompanyPayoutSchedule extends HydraMember {
  id: string;
  type?: string;
  interval?: number;
  minimumThreshold?: number;
  maximumPayout?: number;
}

export interface CompanyPayout extends HydraMember {
  id: string;
  driver?: Driver;
  licenseSeatRegistration?: LicenseSeatRegistration;
  status?: string;
  comment?: string;
  includeIntoBankDocument?: boolean;
  executionTimestamp?: Date;
  paidDate?: Date;
  entries?: Array<CompanyPayoutEntry>;
}

export interface CompanyPayoutEntry extends HydraMember {
  id: string;
  payout?: CompanyPayout;
  type?: string;
  amount?: number;
  subject?: string;
  comment?: string;
}

export interface Company extends HydraMember {
  id: string;
  name: string;
  industry?: string;
  size?: string;
  contactEmail?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contactTelephone?: string | null;
  contactLanguage?: string | null;
  contactAddress?: Address;
  deleted: boolean;
  url: string;
  licenses: License[];
}

export interface User extends HydraMember {
  id: string;
  salutation: string;
  email: string;
  address: Address;
  avatar: WbFile;
  roles: string[];
  firstName: string;
  lastName: string;
  fullName: string;
  language: string;
  phoneNumber: string | null;
  birthday: string | Date;
  twoFactorEnabled: boolean;
  lastLogin: string;
  active: boolean;
  emailVerifiedOn: string | Date;
}

export interface Box extends HydraMember {
  firstCampaign: Campaign;
  status: string;
  type: string;
  mountingDate?: Date;
  unmountingDate?: Date;
  sealNumber?: string;
  alreadyPayed?: number;
  totalPrice?: number;
  depot?: number;
  variant?: 'unknown' | 'with_base' | 'without_base';
  keyNumber?: string;
  stable?: boolean | null;
  boxBaseSurface?: 'driver_de' | 'driver_en' | 'driver_fr' | 'driver_it' | 'customer_de' | 'customer_en' | 'customer_fr' | 'customer_it' | 'unknown';
}

interface EntityChangeCommon extends HydraMember {
  entityId: string,
  className: string,
  date: Date,
  user: RequireOnly<User, 'firstName' | 'lastName' | 'roles'>,
}

interface EntityChangeU extends EntityChangeCommon {
  type: 'update',
  changes: { [key: string]: (string | boolean | number)[]; },
}

interface EntityChangeCD extends EntityChangeCommon {
  type: 'create' | 'delete',
  changes: { [key: string]: string | boolean | number; },
}

export type EntityChange = EntityChangeU | EntityChangeCD;

export interface Mounting extends HydraMember {
  id: string;
  campaign?: Campaign;
  region?: Region;
  address: string;
  gisLocation: Location;
  archived?: boolean;
  forBusinessOnly?: boolean;
  company?: Company | string;
  license?: License | string;
  updatedAt: Date | string;
}

export interface MountingTimeslot extends HydraMember {
  id: string;
  mounting: Mounting;
  size: number;
  duration: number;
  startTime: Date;
  active: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Comment {}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Rank {}

export interface MountingTimeslotRegistration extends HydraMember {
  id: string;
  timeslot: MountingTimeslot;
  campaignRegistration: CampaignRegistration;
  driver: Driver;
  commentAuthor?: User;
  box?: Box;
  oldBox?: Box;
  status: 'pending' | 'completed' | 'canceled' | 'missed' | 'other';
  comment?: string;
  adminComment?: string;
  type: 'mounting' | 'unmounting' | 'change' | 'fix';
  approved: boolean;
  phoneModel: string;
  returnType: 'v1' | 'v2_with_key' | 'v2_no_key';
}

export interface Comment {}

export interface Rank {}

export interface Driver extends HydraMember {
  user: User;
  amountOfCampaignRegistrations: number;
  requestedLoyaltyPointReward: boolean;
  avgSpeed?: number;
  bankName: string;
  bicycleType?: string;
  box?: Box;
  co2Saved: number;
  createdAt: string;
  defaultDestinationAddress: Address;
  defaultSourceAddress: Address;
  estAvgKmPerDay: number;
  estToursPerWeek: number;
  helperDriver: boolean;
  iban: string;
  id: string;
  kcalSpent: number;
  latestMountingTimeslotRegistration?: MountingTimeslotRegistration;
  noticeComment?: Comment;
  caredForAt?: Date | null;
  numOfTours: number;
  payee: string;
  profileComplete: boolean;
  liabilityDisclaimerSigned: boolean;
  rankLastWeek: number;
  rankStatus?: Rank;
  region?: Region;
  specialDriverPhoto: boolean;
  specialDriverStanding: boolean;
  timestampLastRide: string | null;
  totalDrivenDistance: number;
  totalDrivenTime: number;
  updatedAt: string | null;
  totalPoints: number;
  yearOfFirstPayout?: number;
  warningLevel?: 'none' | 'first' | 'second' | 'third';
  outstandingDepot?: number;
  acceptedAgbVersions?: { registration?: number, privacy?: number };
  endDateLastRegisteredCampaign?: string;
  endDateLastFinishedCampaign?: string;
  verificationRequested?: boolean;
  licenseSeatRegistrations?: LicenseSeatRegistration[];
  licenseSeat?: LicenseSeat;
  companyProfileComplete?: boolean;
  anonymousRanking?: boolean;
  canton?: string;
  luggageRackUsable?: boolean;
  saddlebags?: boolean;
  luggageRackInBack?: boolean;
  batteryPosition?: string;
  driverTypeAtRegistration?: string;
}

export interface CampaignGroupTranslations extends TranslationEntity {
  title: string;
  text: string;
}

export interface CampaignTranslations extends TranslationEntity {
  title: string;
  text: string;
}

export interface AdditionalPhoto extends HydraMember {
  id: string;
  file: WbFile;
}

export interface CampaignGroup extends TranslatedEntity<CampaignGroupTranslations> {
  name: string;
  title: string;
  description: string;
  ownerCompany: Company;
  additionalPhotos: AdditionalPhoto[];
  contactEmail: string;
  contractConfirmed: boolean;
  expressBooking: boolean;
  bookingNumbers: { booked: number, slots: number };
  defaultDriversCount?: number;
  defaultQualifiedDriversFilter?: Filter;
  defaultPictureFront?: WbFile;
  defaultPictureLeft?: WbFile;
  defaultPictureRight?: WbFile;
  defaultPictureTop?: WbFile;
  defaultPictureBack?: WbFile;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
  translations: CampaignGroupTranslations[];
  qrToken: string;
  id: string;
  regions?: Region[];
  campaigns?: Campaign[];
}

export interface DriverCommentType extends HydraMember {
  text: string;
  deleted: boolean;
  id: string;
}

export interface Reward extends HydraMember {
  rewardType: 'top' | 'photo' | 'focusPoint';
}

export interface DriverComment extends HydraMember {
  driver: Driver;
  author: User;
  type: DriverCommentType;
  text: string;
  id: string;
  createdAt: Date;
  attachments: DriverCommentAttachment[];
  pinned: boolean;
}

export interface DriverCommentAttachment extends HydraMember {
  file: WbFile;
  driverComment?: DriverComment;
}

export interface DesignFile extends HydraMember {
  campaign: Campaign;
  file: WbFile;
}

export interface Campaign extends TranslatedEntity<CampaignTranslations> {
  group: CampaignGroup;
  name: string;
  driversCount?: number | null;
  qualifiedDriversFilter?: Filter | null;
  active: boolean;
  region: Region;
  pictureFront?: WbFile;
  pictureLeft?: WbFile;
  pictureRight?: WbFile;
  pictureTop?: WbFile;
  pictureBack?: WbFile;
  designPreviews?: DesignFile[];
  startDate?: Date | null;
  endDate?: Date | null;
  translations: CampaignTranslations[];
  bookingNumbers: { booked: number, slots: number };
  id: string;
  trackedDistance: number;
  trackedTime: number;
  avgSpeed: number;
  numOfTours: number;
  numOfTopPhotos: number;
  numOfDriversWithBox: number;
  actualStartDate: Date;
  actualEndDate: Date;
  additionalPhotos: AdditionalPhoto[];
}

export interface CampaignOpt {
  label: string;
  value: string;
  actualStartDate: Date;
  actualEndDate: Date;
}

export interface CampaignRegistration extends HydraMember {
  driver: Driver;
  campaign: Campaign;
  specialDriverAssignments: SpecialDriverAssignment[];
  drivenDistance: number;
  drivenTime: null;
  status: string;
  // eslint-disable-next-line no-use-before-define
  tours: Array<Tour>;
  id: string;
}

export interface CampaignCandidate extends HydraMember {
  driver: Driver;
  campaign: Campaign;
  specialDriverAssignments: SpecialDriverAssignment[];
  drivenDistance: number;
  drivenTime: null;
  status: string;
  declineReason: string;
  comment: string;
  answerTime: Date;
  updatedTime: Date;
  // eslint-disable-next-line no-use-before-define
  tours: Array<Tour>;
  id: string;
}

export type SpecialDriverAssignmentType = 'standing' | 'photo';

export interface SpecialDriverAssignment extends HydraMember {
  id: string;
  campaignRegistration: CampaignRegistration;
  type: SpecialDriverAssignmentType;
  completed: boolean;
}

export interface CampaignWaitingListRegistration extends HydraMember {
  driver: Driver;
  campaign: Campaign;
  id: string;
}

export interface Tour extends HydraMember {
  driver: Driver;
  campaignRegistration: CampaignRegistration | null;
  licenseSeatRegistration: LicenseSeatRegistration | null;
  distance: number;
  time: number;
  startPosition: Position;
  endPosition: Position | null;
  id: string;
  createdAt: Date;
  averageSpeed: number;
  kcal: number;
  co2: number;
}

export interface Position extends HydraMember {
  lat: number;
  lng: number;
  t: string;
  tid: string;
}

export interface PayoutEntry extends HydraMember {
  payout: Payout | null;
  amount: number;
  subject: string;
  comment: string | null;
  subType: number;
  type: 'week' | 'reward' | 'box' | 'manual' | 'depot' | 'credit' | 'debt';
}

export interface PayoutCreditEntry extends HydraMember {
  amount: number;
  status: 'new' | 'pending' | 'inPayment' | 'payed' | 'error';
  debt: boolean;
  comment: string;
  subject: string;
  author: User;
  driver: Driver;
  onlyForCampaignRegistration: CampaignRegistration | null;
  payoutEntry: PayoutEntry | null;
}

export interface Payout extends HydraMember {
  id: string;
  driver: Driver;
  campaignRegistration: CampaignRegistration;
  entries: PayoutEntry[];
  status: string;
  comment: string;
  includeIntoBankDocument: boolean;
  executionTimestamp: Date | null;
  paymentAmount?: number;
}

export interface Download extends HydraMember {
  id: string;
  file: WbFile;
  owner: User;
}

export interface VideoCategoryTranslation extends TranslationEntity {
  id: string;
  title: string;
  description: string;
  language: string;
}

export interface VideoCategory extends TranslatedEntity<VideoCategoryTranslation> {
  id: string;
  orderNumber: number;
  target: string;
}

export interface Video extends HydraMember {
  id: string;
  title: string;
  description: string;
  file: WbFile;
  language: string;
  categories: VideoCategory[];
  orderNumber: number;
}

export interface FaqSectionTranslation extends TranslationEntity {
  id: string;
  sectionId: FaqSection;
  language: string;
  title: string;
}

export interface FaqSection extends TranslatedEntity<FaqSectionTranslation> {
  id: string;
  slug: string;
  showInApp: boolean;
  showInForBusinessApp: boolean;
}

export interface FaqQuestionTranslation extends TranslationEntity {
  id: string;
  faqQuestionId: FaqQuestion;
  language: string;
  question: string;
  answer: string;
}

export interface FaqQuestion extends TranslatedEntity<FaqQuestionTranslation> {
  id: string;
  section: FaqSection;
  orderNumber: number;
}

export interface Statistics {
  totalDistance: number;
  totalDrivingTime: number;
  totalCo2: number;
  totalAmountOfDrivers: number;
  totalCompleteDriverProfiles: number;
  totalActiveBoxes: number;
}

export interface MonitoringDriverVerification extends HydraMember {
  driverId: string;
  campaignId: string;
  campaignGroupId: string;
  regionId?: string;
  region: string;
  email: string;
  firstName: string;
  lastName: string;
  campaignName: string;
  campaignStartDate: string;
  createdAt?: string;
}

export interface MonitoringCampaignVerification {
  id: string;
  campaignId: string;
  campaignGroupId: string;
  regionId?: string;
  region: string;
  campaignName: string;
  campaignStartDate: string;
  totalDrivers: number;
  missingVerifications: number;
  verificationPercentage: number;
  createdAt?: string;
}

export interface PayoutAdditional extends HydraMember {
  paymentType?: 'general' | 'campaign';
  showInApp?: boolean;
  status?: 'open' | 'paid';
  id: string;
  driverId: string;
  campaignId?: string;
  campaignGroupId?: string;
  payoutId?: string;
  reason: string;
  amountChf: number;
  weekSlot?: number;
  week1AmountChf?: number | null;
  week2AmountChf?: number | null;
  week3AmountChf?: number | null;
  week4AmountChf?: number | null;
  week1Amount?: number | null;
  week2Amount?: number | null;
  week3Amount?: number | null;
  week4Amount?: number | null;
  paymentListId?: string;
  notes?: string;
  exported: boolean;
  exportedAt?: string;
  exportRef?: string;
  recordedByUserId: string;
  recordedAt: string;
  createdAt?: string;
  updatedAt?: string;
  driverFirstName: string;
  driverLastName: string;
  driverEmail: string;
  campaignName?: string;
  recordedByName: string;
}

export interface FocusPointTemplate extends HydraMember {
  name: string;
  gisLocation: Location;
  region: Region;
  radius: number;
  threshold?: number;
  cardinality?: number;
  paymentAmount?: number;
  numOfPhotos?: number;
  validityStart?: Date;
  validityEnd?: Date;
}

export interface FocusPoint extends HydraMember {
  name: string;
  gisLocation: Location;
  radius: number;
  threshold?: number;
  cardinality?: number;
  paymentAmount?: number;
  numOfPhotos?: number;
  campaign?: Campaign;
  validityStart?: Date;
  validityEnd?: Date;
  statistics: {
    totalStops: number;
    totalTime: number;
    totalPhotos: number;
  };
  template?: FocusPointTemplate;
}

export interface StatisticPointTemplate extends HydraMember {
  name: string;
  gisLocation: Location;
  region: Region;
  radius: number;
  campaign?: Campaign;
  validityStart?: Date;
  validityEnd?: Date;
}

export interface StatisticPoint extends HydraMember {
  name: string;
  gisLocation: Location;
  radius: number;
  campaign?: Campaign;
  validityStart?: Date;
  validityEnd?: Date;
  statistics?: {
    time: number;
    stops: number;
  };
  template?: StatisticPointTemplate;
}

export interface FocusPointLedger extends HydraMember {
  focusPoint: FocusPoint;
}

export interface Photo extends HydraMember {
  owner?: Driver;
  file?: WbFile;
  rating?: number;
  gisLocation?: { lat: number, long: number };
  location?: string;
  creationTimestamp?: Date;
  focusPointLedger?: FocusPointLedger;
  campaignRegistration?: CampaignRegistration;
  licenseSeatRegistration?: LicenseSeatRegistration;
}

export interface Verification extends HydraMember {
  driver: Driver;
  pictureFront?: WbFile;
  pictureLeft?: WbFile;
  pictureRight?: WbFile;
  pictureTop?: WbFile;
  pictureBack?: WbFile;
  status: 'NeedsVerification' | 'Verified' | 'Denied';
  campaignRegistration?: CampaignRegistration;
  licenseSeatRegistration?: LicenseSeatRegistration;
  createdAt: Date;
  oldestFileCreatedAt: Date | null;
}

export interface PointLedger extends HydraMember {
  driver: Driver;
  tour: Tour;
  interestArea?: InterestZone;
  author?: User;
  amount: number;
  timestamp: Date;
  type: 'zone' | 'zone_standing' | 'longday' | 'admin' | 'other';
  comment?: string;
  pointReward?: PointReward;
}

export interface PointReward extends TranslatedEntity<PointRewardTranslation> {
  id: string;
  name: string;
  cost: number;
  image?: WbFile | null;
  validityStart?: Date | null;
  validityEnd?: Date | null;
  initialStock?: number | null;
  userQuota?: number | null;
  promoted?: boolean;
  orderIndex?: number;
  regions?: Region[]|null;

}

export interface PointRewardTranslation extends TranslationEntity {
  title: string;
  shortDescription: string;
  description: string;
}

export interface AgbTranslation extends TranslationEntity {
  text: string;
}

export interface Agb extends TranslatedEntity<AgbTranslation> {
  name: string;
  versionNumber: number;
  versionDate: Date | string;
  versioned: boolean;
}

export interface NotificationPlanTranslation extends TranslationEntity {
  notificationPlanTransport: NotificationPlanTransport;
  title: string;
  message: string;
  short?: string;
  emailTemplate?: string;
}

export type NotificationPlanTransport = TranslatedEntity<NotificationPlanTranslation>

export interface NotificationPlanHandlebarTranslation extends TranslationEntity {
  value: string;
}

export interface NotificationPlanHandlebar extends TranslatedEntity<NotificationPlanHandlebarTranslation> {
  notificationPlan: NotificationPlan;
  key: string;
}

export type NotificationTrigger =
  'signup'
  | 'mounting_approved'
  | 'reward_payout_paid'
  | 'campaign_registration'
  | 'profile_complete'
  | 'license_registration'
  | 'license_reward_payout_paid';

export interface NotificationPlanBase<T> extends HydraMember {
  name: string;
  receiversFilter?: Filter;
  campaigns?: Campaign[];
  campaignFilter?: Filter;
  licenses?: License[];
  campaignConnection?: 'no' | 'fix' | 'filter' | 'fix_license' | 'filter_license';
  active: boolean;
  sms?: NotificationPlanTransport;
  push?: NotificationPlanTransport;
  email?: NotificationPlanTransport;
  zenDeskSubject?: string;
  zenDeskMessage?: string;
  historyEntryText?: string | null;
  historyEntryType?: string | DriverCommentType | null;
  dateType: T;
  dateData: T extends 'fix' ? { dates: string[] } :
    T extends 'recurrent' ? { startDate: Date, endDate?: Date, recurrence: string } :
      T extends 'event' ? { basePercentage: number, modifier: number }
        : { trigger: NotificationTrigger, modifier: number };
  customHandlebars?: NotificationPlanHandlebar[];
}

export type NotificationPlan =
  NotificationPlanBase<'fix'>
  | NotificationPlanBase<'recurrent'>
  | NotificationPlanBase<'event'>
  | NotificationPlanBase<'triggered'>;

export interface Notification extends HydraMember {
  notificationPlan: NotificationPlan;
  driver: Driver;
  scheduledAt: Date;
  sentAt?: Date;
  openedAt?: Date;
  status: 'sent' | 'error';
  transport: 'sms' | 'push' | 'email';
  title: string;
  message: string;
}

export interface HistoryGeneral extends HydraMember {
  region?: Region | null;
  recordingDate: Date;
  startDate: Date;
  endDate: Date;
}

export interface HistoryDriver extends HydraMember {
  region?: Region | null;
  recordingDate: Date;
  startDate: Date;
  endDate: Date;
}

export interface HistoryUsageStatistic extends HydraMember {
  recordingDate: Date | string;
  source: string;
  weekStartUtc: Date | string;
  weekEndUtc: Date | string;
  calendarYear: number;
  calendarWeek: number;
  distinctUsers: number;
  uniqueUsersLastDay: number;
  uniqueUsersLast7Days: number;
  uniqueUsersLast30Days: number;
  totalRequests: number;
  generatedAtUtc: Date | string;
  createdAt?: Date | string | null;
}

export interface VisibilityPlanningCapacityDto {
  availableBicycles: number | null;
  usedBicycles: number | null;
  usedBicyclesOnLocation: number | null;
  totalBicycles: number | null;
  region: Region | null;
  startTime: Date | null;
  endTime: Date | null;
}

export interface TimeRange {
  start: Date | null;
  end: Date | null;
}
export interface VisibilityBicycleDetail {
  label: string;
  value: string;
  gisLocation: {
    lat: number | undefined;
    long: number | undefined;
  };
  visibilityBicycle: VisibilityBicycle;
  visibilityTracker: VisibilityTracker;
  selected: boolean;
}

export interface VisibilityPlanning extends HydraMember {
  id: string;
  region?: Region | null | undefined;
  campaign?: Campaign | null | undefined;
  location?: VisibilityLocation | null | undefined;
  visibilityBicycles?: EnrichedVisibilityBicycleOption[] | VisibilityBicycle[] | undefined;
  startTime?: Date | null | undefined;
  endTime?: Date | null | undefined;
  status?: VisibilityPlanningStatus | undefined;
  bicycleCount?: number | undefined;
  comment?: string | undefined;
  newDesign?: string | undefined;
  createdAt?: Date | undefined;
  updatedAt?: Date | undefined;
  responsibleUserForTransfer?: string | undefined;
}

export interface VisibilityPlanningForm {
  '@id': string;
  '@type': string;
  id: string;
  region: string | undefined;
  campaign: string | undefined;
  location: string | undefined;
  visibilityBicycles: (string | VisibilityBicycleDetail)[]; // Either string or an object containing visibilityBicycle details
  startTime?: Date | null | undefined;
  endTime?: Date | null | undefined;
  status?: VisibilityPlanningStatus | undefined;
  bicycleCount?: number | undefined;
  comment?: string | undefined;
  newDesign?: string | undefined;
  createdAt?: Date | undefined;
  updatedAt?: Date | undefined;
  time: TimeRange;
  responsibleUserForTransfer?: string | undefined;
}

export interface VisibilityPlanningDirectForm {
  region?: string;
  visibilityBicycle: string;
  entries: VPTimespanEntry[];

  bicycleCount?: number | undefined;
}

export interface VPTimespanEntry {
  _id: string
  campaign?: string
  location?: string
  responsibleUserForTransfer?: string
  time: { start: Date|null; end: Date|null }
  newDesign: string
  comment: string
}

export interface VisibilityPlanningFormEmpty {
  campaign: undefined;
  region: undefined;
  location: undefined;
  visibilityBicycles: (VisibilityBicycle | EnrichedVisibilityBicycleOption)[];
  bicycleCount: number;
  bicycleCountPlanned: number;
  startTime: undefined;
  endTime: undefined;
  status: VisibilityPlanningStatus;
  newDesign: string;
  comment: string;
  createdAt: undefined;
  updatedAt: undefined;
  time: TimeRange;
  responsibleUserForTransfer?: undefined;
}

// Union type for the full object
export type VisibilityPlanningFormUnion = VisibilityPlanningForm | VisibilityPlanningFormEmpty;

export interface VisibilityMapState {
  locationStack: StackMapLocation[],
  currentPosition: { lat: number; lng: number } | null,
  showCurrentPosition: boolean;
  center: any;
}

export interface VisibilityTransfer extends HydraMember {
  id: string;
  region?: Region | null;
  campaign?: Campaign | null;
  visibilityPlanning: VisibilityPlanning | null;
  visibilityBicycle?: VisibilityBicycle | null;
  transferTime?: Date | null;
  actualTransferTime?: Date | null;
  newLocation?: VisibilityLocation | null;
  oldLocation?: VisibilityLocation | null;
  responsibleUser?: User | null;
  photos?: Photo[] | null;
  newDesign: string;
  comment: string;
  done: boolean;
  createdAt?: string;
  updatedAt?: string;
  // done props
  confirmationTime: Date | null;
  confirmedByUser: User | null;
}

export interface VisibilityTransferMapData {
  id: string;
  done: boolean;
  transfer: VisibilityTransfer;
  locNewName: string;
  locOldName: string;
  gisLocNew?: Location;
  gisLocOld?: Location;
  radius: number;
}

export interface VisibilityBicycle extends HydraMember {
  id: string;
  name: string;
  user?: User | null;
  responsible?: User | null;
  region?: Region | null;
  number: string;
  boxNumber: string;
  gpsTrackerId: number | null;
  gpsTrackerNumber: number | null;
  brand: string;
  color: string;
  status: VisibilityBicycleStatus;
  comment: string;
  createdAt?: string;
  updatedAt?: string;
  visibilityPlannings: VisibilityPlanning[];
}

export interface VisibilityBicycleOption extends VisibilityBicycle {
  value: string,
  label: string,
}

export interface VisibilityPhoto extends HydraMember {
  id: string;
  owner?: User | null;
  file?: WbFile;
  rating?: number;
  gisLocation?: { lat: number, long: number };
  location?: string;
  creationTimestamp?: Date;
  visibilityLocation?: VisibilityLocation;
  visibilityTransfer?: VisibilityTransfer;
  campaignRegistration?: CampaignRegistration;
}

export interface VisibilityLocation extends HydraMember {
  id: string;
  region: Region;
  name: string;
  status: VisibilityLocationStatus;
  radius: number;
  quota: number;
  comment: string;
  gisLocation: { lat: number, long: number };
  createdAt?: string;
  updatedAt?: string;
}

export interface VisibilityLocationOption extends VisibilityLocation {
  selected: boolean;
  gisLocation: Location;
  name: string;
  value: string;
  label: string;
}

export interface VisibilityTracker {
  inUse?: boolean;
  imei?: string;
  iccid?: string;
  loraId?: string;
  imsi?: string;
  btMac?: string;
  btMacAddress?: string;
  uwbId?: string;
  serial?: string;
  type?: string;
  expires?: string; // Assuming ISO date string
  lastConnection?: string; // Assuming ISO date string
  firstSeen?: string; // Assuming ISO date string
  mode?: string;
  name?: string;
  batteryVoltage?: number;
  batteryPct?: number;
  batteryType?: string;
  tags?: string[];
  currentMode?: string;
  color?: string;
  saveToWeb?: boolean;
  active?: boolean;
  deepSleep?: boolean;
  simstate?: string;
  stripeSubscription?: string;
  subscriptionType?: string;
  meta?: Record<string, any>; // Assuming metadata object
  siloId?: string;
  resellerId?: string;
  resellerPlanId?: string;
  btPasskey?: string;
  simType?: string;
  id?: number;
  ownerId?: string;
  onoWhitelistId?: string;
  latestPoints?: TrackerPoint[];
}

export interface VisibilityTrackerOption extends VisibilityTracker {
  gisLocation?: Location;
  tracker?: VisibilityTracker;
  name?: string;
  value: string | number;
  label: string;
}

export interface EnrichedVisibilityBicycleDto extends HydraMember {
  visibilityBicycle: VisibilityBicycle;
  visibilityTracker: VisibilityTracker;
  currentLocationForBicycle: VisibilityLocation;
  oldLocationForBicycle: VisibilityLocation;
}

export interface EnrichedVisibilityBicycleOption extends EnrichedVisibilityBicycleDto {
  selected: boolean;
  gisLocation: Location;
  name: string;
  value: string;
  label: string;
}

export interface StackMapLocation {
  id: string;
  name: string;
  lat: number;
  lng: number;
}

export interface TrackerPoint {
  id: number;
  timestamp: string; // ISO date string
  location: {
    lng: number;
    lat: number;
  };
  speed: number;
  course: number;
  accuracy: number;
  sendReason: number;
  address: string;
  deviceId: number;
  alertType: number;
}

export enum VisibilityPlanningStatus {
  DRAFT = 'draft',
  CONFIRMED = 'confirmed',
  // Add other statuses if needed
}

export enum VisibilityPlanningModalMode {
  NEW = 'new',
  EDIT = 'edit',
  CONFIRM = 'confirmation',
  VIEW = 'view',
}

export enum VisibilityPlanningMode {
  DRAFT = 'draft',
  PASSED = 'passed',
  CONFIRM = 'confirmed',
}

export enum VisibilityTransferMode {
  OPEN = 'open',
  DONE = 'done',
}

export enum VisibilityBicycleViewMode {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ARCHIVED = 'archived',
}

export enum VisibilityLocationViewMode {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ARCHIVED = 'archived',
}

export enum VisibilityLocationStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ARCHIVED = 'archived'
}

export enum VisibilityBicycleStatus {
  ACTIVE = 'active',
  DEFECTIVE = 'defective',
  STOLEN = 'stolen',
  IN_MAINTENANCE = 'in_maintenance',
  LOST = 'lost',
  RESERVED = 'reserved',
  IN_TRANSIT = 'in_transit',
  DECOMMISSIONED = 'decommissioned',
}

export interface InterestZone extends HydraMember {
  name: string;
  gisLocation: { lat: number, long: number };
  region: Region;
  radius: number;
  multiplier: number;
  type: 'distance' | 'time';
  threshold: number | null;
  validityStart?: Date;
  validityEnd?: Date;
  totalLedgerEntryAmount?: number;
  totalLedgerEntryPoints?: number;
}

export interface InterestAreaLedger extends HydraMember {
  interestArea: InterestZone;
  tour: Tour;
  tour2: Tour | null;
  amount: number | null;
  pointLedgerEntry: PointLedger;
}

export interface DistributionStatistics {
  averageAmountOfToursPerWeek: {
    count: number;
    range: number;
  }[];
  averageDistancePerWeek: {
    count: number;
    range: number;
  }[];
  driversPerAge: {
    amountOfDrivers: number;
    age: number;
  }[];
  driversPerBicycleType: {
    amountOfDrivers: number;
    bicycleType: string;
  }[];
  driversPerGender: {
    amountOfDrivers: number;
    salutation: string;
  }[];
  driversPerRegion: {
    amountOfDrivers: number;
    region: { name: string };
  }[];
}

export interface Setting extends HydraMember {
  name: string;
  values: object;
}

export interface LogicStatement<T = Record<string, unknown>> extends HydraMember {
  name: string;
  data: T;
}

export interface Filter extends HydraMember {
  logicGroups: LogicGroup[];
}

// eslint-disable-next-line max-len
const LogicStatementTypes = ['numberRange', 'boolean', 'nullableEnum', 'enum', 'nullableChoice', 'choice', 'dateRange', 'search', 'textArea', 'number'] as const;

export type LogicStatementType = typeof LogicStatementTypes[number];

export interface FilterPrototype<T = Record<string, string>, S = Record<string, string>> {
  type: LogicStatementType;
  declaration: {
    data: T;
    conditional: boolean;
    conditionalLicense: boolean;
  };
  options: S;
}

export interface FilterPrototypeList {
  [T: string]: FilterPrototype;
}

export interface LogicGroup extends HydraMember {
  logicStatements: LogicStatement[];
}

export type MaybeHydra<T extends HydraMember> = DeepPartialOnlyKeys<T, keyof HydraMember>;

export interface TableFieldsEntry extends HydraMember {
  user: RequireOnly<User, 'id'>;
  activeFields: string[];
  pathKey: string;
}

export interface WorkingTableFieldsEntry {
  id: string;
  activeFields: string[];
}

export enum DriverHistoryType {
  INCOMPLETE = 'Incomplete',
  BUSINESS = 'Business',
  STANDARD = 'Standard',
  MIXED = 'Mixed',
  ALL = 'All'
}

export interface Advertisement extends TranslatedEntity<AdvertisementTranslation> {
  name: string;
  company: Company;
  showInApp: boolean;
  showInForBusiness: boolean;
  validityStart: Date|null;
  validityEnd: Date|null;
  places: AdvertisementPlace[];
  regions: Region[];
  translations: AdvertisementTranslation[];
}

export interface AdvertisementTranslation extends TranslationEntity {
  title: string;
  description: string;
  image: WbFile|any|null;
  url: string;
}

export interface AdvertisementPlace extends HydraMember{
  spot: AdvertisementSpot;
}

export enum AdvertisementSpot {
  DASHBOARD_DASHBOARD = 'dashboard_dashboard',
  DASHBOARD_RANKING = 'dashboard_ranking',
  DASHBOARD_STATISTICS = 'dashboard_statistics',
  TRACKING_SCREEN = 'tracking_screen',
}

export interface LicenseSeatRegistration extends HydraMember {
  id: string;
  licenseSeat?: LicenseSeat;
  driver?: Driver;
  startDate?: Date;
  endDate?: Date;
  status?: string;
}

export enum PayoutStatus {
  PENDING = 'pending',
  INPAYMENT = 'inPayment',
  PAYED = 'payed',
  ERROR = 'error',
}

export interface Segmentation {
  region: string | null;
  gisLocation: {
    lat: number | null;
    long: number | null;
  };
  radius: number | null;
  startDate: Date | null;
  endDate: Date | null;
}

export interface SegmentedDriver {
  driver: Driver | null;
  startMatches: number | null;
  endMatches: number | null;
}
